import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The email and password sign-in form. Owns only its input state and the last
// server error; success hands control back to the caller. `fetch` is the test
// seam. See ADR-0042.

type LoginFormProps = {
  onLoggedIn: () => void;
  // Pre-filled address (an invitee arriving from an invitation page).
  initialEmail?: string;
};

/**
 * Signs the person in with email and password through the auth library and
 * calls `onLoggedIn` on success. Shows the server's own error text, which is
 * the same for unknown, unverified and wrong-password. Rendered by the public
 * entry at `/login`.
 */
export function LoginForm({ onLoggedIn, initialEmail = "" }: LoginFormProps): JSX.Element {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authPost("/sign-in/email", { email, password });
      onLoggedIn();
    } catch (err) {
      // The server answers unknown, unverified and wrong-password with the
      // same words; the UI shows exactly what it said.
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-card">
      <form onSubmit={onSubmit} aria-label="Sign in">
        <h1>Sign in</h1>
        <p className="lede">
          Welcome back. New here?{" "}
          <a href="/sign-up">Create an Organisation</a>.
        </p>
        <div className="field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            name="email"
            autoComplete="username"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error ? (
          <div className="notice bad" role="alert">
            <div>{error}</div>
          </div>
        ) : null}
        <div className="btn-row">
          <button type="submit" className="btn primary lg block" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </div>
        <p className="hint">
          <a href="/forgot-password">Forgot your password?</a>
          {" · "}
          <a href="/login/sso">Sign in with SSO</a>
        </p>
      </form>
    </div>
  );
}
